/**
 * Аудиты организации и присвоение публичного аудита.
 *
 * Присвоение — это переход из воронки в продукт: бесплатный аудит запускается
 * без всякой сессии, живёт семь дней по публичной ссылке и никому не
 * принадлежит. `POST /audits/claim` привязывает его к организации.
 *
 * Право на присвоение контракт даёт **не тому, у кого ссылка**. Ссылка публична
 * и пересылается: по ней аудит смотрят, а не забирают. Доказательств два, и оба
 * необязательны по отдельности:
 *
 * * `session_token` — токен браузерной сессии, из которой аудит запускали. Он
 *   приходит заголовком `X-Seeno-Audit-Session` **один раз**, в ответе на
 *   запуск, и экран аудита кладёт его в `sessionStorage`;
 * * `verification_token` — токен из письма, то есть подтверждение владения
 *   адресом, на который отчёт отправляли.
 *
 * Кабинет умеет первое: он и есть та вкладка, из которой аудит запускали.
 * Открывший пересланную ссылку не присвоит ничего — сервер ответит `403`, и это
 * не ошибка интерфейса, а работающая защита.
 *
 * Ключ идемпотентности обязателен: присвоение списывает `audits` из квоты
 * организации, а повтор запроса — обычное дело (потеряли ответ, нажали ещё раз).
 */

import { api, type Page } from './client';
import type { components } from './schema';

export type Audit = components['schemas']['Audit'];
export type AuditClaimRequest = components['schemas']['AuditClaimRequest'];
export type QuotaUsage = components['schemas']['QuotaUsage'];

/**
 * Публичный аудит по слагу. Маршрут публичный: сессия ему не нужна и не мешает.
 *
 * `quiet`, потому что 401 здесь ожидаемого смысла не имеет — путь под
 * `/public/`, и объявлять из-за него потерю сессии значит выкидывать вошедшего
 * человека на форму входа с экрана, который вообще не про вход.
 */
export function getPublicAudit(slug: string, signal?: AbortSignal): Promise<Audit> {
  return api.get<Audit>(`/public/audits/${encodeURIComponent(slug)}`, {
    signal,
    quiet: true,
  });
}

/** Аудиты, уже присвоенные организации. */
export function listAudits(signal?: AbortSignal): Promise<Page<Audit>> {
  return api.get<Page<Audit>>('/audits', { signal, query: { limit: 50 } });
}

/**
 * Расход текущего расчётного периода: отсюда берётся счётчик `audits`.
 *
 * Читается **только владельцем** (`/billing/**` в матрице контракта), а
 * присваивать аудит может ещё и аналитик. Поэтому экран спрашивает расход
 * только тогда, когда роль до него дотягивается, а иначе говорит о списании
 * словами. Показывать аналитику пустой счётчик было бы хуже, чем не показывать
 * никакого: пустое место читается как «ноль».
 */
export function getQuotaUsage(signal?: AbortSignal): Promise<QuotaUsage> {
  return api.get<QuotaUsage>('/billing/quota', { signal });
}

/** Присвоить публичный аудит организации. Списывает `audits` из квоты. */
export function claimAudit(body: AuditClaimRequest, key: string): Promise<Audit> {
  return api.post<Audit>('/audits/claim', body, { idempotencyKey: key });
}
